import { useEffect, useState } from 'react';
import type { TimesheetDueConfig, UpdateTimesheetDueConfigInput } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useTimesheetDueConfig, useUpdateTimesheetDueConfig } from './api.js';

const DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Mountain – no DST (Phoenix)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'UTC', label: 'UTC' },
];

const WEEK_OFFSET_OPTIONS = [
  { value: 0, label: 'Same week as the timesheet' },
  { value: 1, label: 'The week after the timesheet' },
  { value: 2, label: 'Two weeks after the timesheet' },
];

const inputClass =
  'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

function toForm(config: TimesheetDueConfig): UpdateTimesheetDueConfigInput {
  return {
    enabled: config.enabled,
    dueDayOfWeek: config.dueDayOfWeek,
    dueTime: config.dueTime,
    timeZone: config.timeZone,
    weekOffset: config.weekOffset,
    remindersEnabled: config.remindersEnabled,
  };
}

/** Admin-only form to configure the global timesheet due date/time and reminder emails. */
export function DueConfigCard(): JSX.Element {
  const query = useTimesheetDueConfig();
  const updateMutation = useUpdateTimesheetDueConfig();
  const [form, setForm] = useState<UpdateTimesheetDueConfigInput | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setForm(toForm(query.data));
  }, [query.data]);

  if (!form) return <></>;

  const dayLabel = DAY_OPTIONS.find((d) => d.value === form.dueDayOfWeek)?.label ?? '—';
  const offsetLabel =
    form.weekOffset === 0
      ? 'that same week'
      : form.weekOffset === 1
        ? 'the following week'
        : `${form.weekOffset} weeks later`;
  const tzLabel = TIMEZONE_OPTIONS.find((t) => t.value === form.timeZone)?.label ?? form.timeZone;

  const handleSave = (): void => {
    setSaved(false);
    updateMutation.mutate(form, { onSuccess: () => setSaved(true) });
  };

  return (
    <div>
      <p className="text-xs text-slate-500">
        Global deadline applied to every timesheet. Drives the due status on this list and the
        on-time / past-due flag captured when a timesheet is submitted.
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300"
          />
          Enabled
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Due day
          <select
            value={form.dueDayOfWeek}
            onChange={(e) => setForm({ ...form, dueDayOfWeek: Number(e.target.value) })}
            className={inputClass}
            disabled={!form.enabled}
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Due time
          <input
            type="time"
            value={form.dueTime}
            onChange={(e) => setForm({ ...form, dueTime: e.target.value })}
            className={inputClass}
            disabled={!form.enabled}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Timezone
          <select
            value={form.timeZone}
            onChange={(e) => setForm({ ...form, timeZone: e.target.value })}
            className={inputClass}
            disabled={!form.enabled}
          >
            {TIMEZONE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Relative to week
          <select
            value={form.weekOffset}
            onChange={(e) => setForm({ ...form, weekOffset: Number(e.target.value) })}
            className={inputClass}
            disabled={!form.enabled}
          >
            {WEEK_OFFSET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <Button onClick={handleSave} isLoading={updateMutation.isPending}>
          Save
        </Button>
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.remindersEnabled}
            onChange={(e) => setForm({ ...form, remindersEnabled: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300"
            disabled={!form.enabled}
          />
          Send reminder emails for unsubmitted timesheets
        </label>
        <p className="mt-1 text-xs text-slate-500">
          Emails the timesheet owner (CC their approvers) at 8am and 4pm on the due day, then at
          8am, noon and 4pm every day while overdue, until the timesheet is submitted. Requires the
          due date above to be enabled.
        </p>
      </div>

      {form.enabled ? (
        <p className="mt-3 text-xs text-slate-500">
          Each timesheet is due on{' '}
          <span className="font-medium text-slate-700">
            {dayLabel} at {form.dueTime}
          </span>{' '}
          ({tzLabel}) of <span className="font-medium text-slate-700">{offsetLabel}</span>.
        </p>
      ) : (
        <p className="mt-3 text-xs text-slate-500">
          Due dates are turned off — no due status is shown.
        </p>
      )}

      {saved ? <p className="mt-2 text-xs text-emerald-600">Saved.</p> : null}
      {updateMutation.isError ? (
        <p className="mt-2 text-xs text-rose-600">Could not save. Please try again.</p>
      ) : null}
    </div>
  );
}
